import React, { useId, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import type { SelectChangeEvent } from '@mui/material/Select';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import useStyles from '../../../styles/alertForm';
import {
  defaultAnalysisPeriodDays,
  defaultDaysOfWeekFilter,
  defaultHoursOfDayFilter,
  getTooltipSlotProps,
  selectOutlineSx,
} from '../helpers';
import { AnalysisOptions, HistoryAnalysisOptions } from './AnalysisOptionsContainer';

interface AnalysisPeriodProps {
  value: AnalysisOptions;
  handleChange: (key: string, val: unknown) => void;
  allowEditing: boolean;
}

// 0 - 23
const HOURS_OF_DAY_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i.toString().padStart(2, '0'),
}));

// Sunday (0) - Saturday (6)
const DAYS_OF_WEEK_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 320,
    },
  },
};

function renderSelectedHours(selected: number[]) {
  if (selected.length === 0) return 'All hours';
  return selected
    .slice()
    .sort((a, b) => a - b)
    .map((h) => h.toString().padStart(2, '0'))
    .join(', ');
}

function renderSelectedDays(selected: number[]) {
  if (selected.length === 0) return 'All days';
  return selected
    .slice()
    .sort((a, b) => a - b)
    .map((d) => DAYS_OF_WEEK_OPTIONS.find((opt) => opt.value === d)?.label ?? d)
    .join(', ');
}

function AnalysisPeriod({ value, handleChange, allowEditing }: AnalysisPeriodProps) {
  const uid = useId();
  const theme = useTheme();
  const { classes } = useStyles();
  const hValue = value as HistoryAnalysisOptions;
  const { analysisPeriodDays } = hValue;

  const selectedHours = hValue.hoursOfDayFilter ?? defaultHoursOfDayFilter;
  const selectedDays = hValue.daysOfWeekFilter ?? defaultDaysOfWeekFilter;

  // Normalizes MUI's multi-select event value (string | string[] when the
  // underlying option values are numbers) into a sorted number[].
  const handleMultiSelectChange = (
    key: 'hoursOfDayFilter' | 'daysOfWeekFilter',
    event: SelectChangeEvent<number[]>
  ) => {
    const { value: raw } = event.target;
    const nums = (typeof raw === 'string' ? raw.split(',') : raw)
      .map(Number)
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    handleChange(key, nums);
  };

  useEffect(() => {
    if (analysisPeriodDays === undefined) {
      handleChange('analysisPeriodDays', defaultAnalysisPeriodDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.formContainer}>
      <Typography variant="body1" sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        within analysis period of{' '}
        <Tooltip title="Days and Hours to be included in the analysis period" placement="top" arrow>
          <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
        </Tooltip>
      </Typography>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Days"
            type="number"
            value={analysisPeriodDays ?? ''}
            onChange={(e) =>
              handleChange(
                'analysisPeriodDays',
                e.target.value === '' ? undefined : Number(e.target.value)
              )
            }
            placeholder="e.g. 7"
            sx={selectOutlineSx(theme)}
            slotProps={getTooltipSlotProps('Previous number of days')}
            disabled={!allowEditing}
            error={analysisPeriodDays === undefined || analysisPeriodDays === 0}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <Tooltip title="Hours to inspect" placement="top" arrow>
              <InputLabel
                id={`${uid}-hoursOfDayFilter`}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Hours of Day Filter <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
              </InputLabel>
            </Tooltip>
            <Select
              labelId={`${uid}-hoursOfDayFilter`}
              label="Hours of Day Filter"
              multiple
              value={selectedHours}
              onChange={(e) =>
                handleMultiSelectChange('hoursOfDayFilter', e as SelectChangeEvent<number[]>)
              }
              renderValue={(selected) => renderSelectedHours(selected as number[])}
              MenuProps={MENU_PROPS}
              sx={selectOutlineSx(theme)}
              disabled={!allowEditing}
            >
              {HOURS_OF_DAY_OPTIONS.map((h) => (
                <MenuItem key={h.value} value={h.value}>
                  <Checkbox
                    size="small"
                    checked={selectedHours.includes(h.value)}
                    disabled={!allowEditing}
                  />
                  <ListItemText primary={h.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <Tooltip title="Days to inspect in week" placement="top" arrow>
              <InputLabel
                id={`${uid}-daysOfWeekFilter`}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Days of Week Filter <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
              </InputLabel>
            </Tooltip>
            <Select
              labelId={`${uid}-daysOfWeekFilter`}
              label="Days of Week Filter"
              multiple
              value={selectedDays}
              onChange={(e) =>
                handleMultiSelectChange('daysOfWeekFilter', e as SelectChangeEvent<number[]>)
              }
              renderValue={(selected) => renderSelectedDays(selected as number[])}
              MenuProps={MENU_PROPS}
              sx={selectOutlineSx(theme)}
              disabled={!allowEditing}
            >
              {DAYS_OF_WEEK_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  <Checkbox
                    size="small"
                    checked={selectedDays.includes(d.value)}
                    disabled={!allowEditing}
                  />
                  <ListItemText primary={d.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AnalysisPeriod;
